import { analytics } from "./index";
import type { GA4Item } from "./google";
import type { ApiCart } from "@/types";

/**
 * Le tunnel de commande web, vu par Google Analytics.
 *
 * Jusqu'au 1er octobre 2026, le site n'envoyait que des pages vues : GA4 ne
 * voyait aucun ajout au panier, aucune commande. On savait combien de visites
 * venaient de Google, jamais combien de commandes. Les noms sont ceux de l'app
 * mobile (`add_to_cart`, `view_cart`, `begin_checkout`, `order_created_success`,
 * `purchase`) pour qu'un même tunnel se lise web et app ensemble.
 *
 * `analytics.trackEvent` les répartit vers Google, Meta, PostHog et Amplitude
 * selon le plan de tracking. Rien ici ne doit jamais faire échouer un achat :
 * chaque appel est protégé.
 */

const CURRENCY = "DZD";

function safely(fn: () => void): void {
  try {
    fn();
  } catch {
    // La mesure ne doit jamais casser le tunnel.
  }
}

function cartItems(cart: ApiCart): GA4Item[] {
  return (cart.items ?? []).map((i) => ({
    item_id: i.product_ulid,
    item_name: i.product_name,
    affiliation: cart.store_name,
    price: Number(i.quantity ? i.subtotal / i.quantity : i.discounted_price) || 0,
    quantity: i.quantity,
  }));
}

export interface AddToCartTracking {
  name: string;
  /** Prix unitaire affiché, en DA. */
  price: number;
  storeName?: string;
}

export function trackAddToCart(
  productUlid: string,
  quantity: number,
  storeUlid: string,
  info: AddToCartTracking,
): void {
  safely(() =>
    analytics.trackEvent("add_to_cart", {
      currency: CURRENCY,
      value: info.price * quantity,
      store_ulid: storeUlid,
      items: [
        {
          item_id: productUlid,
          item_name: info.name,
          affiliation: info.storeName,
          price: info.price,
          quantity,
        },
      ],
    }),
  );
}

export function trackViewCart(cart: ApiCart, total: number): void {
  safely(() =>
    analytics.trackEvent("view_cart", {
      currency: CURRENCY,
      value: total,
      store_ulid: cart.store_ulid,
      items: cartItems(cart),
    }),
  );
}

export function trackBeginCheckout(cart: ApiCart, total: number, coupon?: string): void {
  safely(() =>
    analytics.trackEvent("begin_checkout", {
      currency: CURRENCY,
      value: total,
      store_ulid: cart.store_ulid,
      ...(coupon ? { coupon } : {}),
      items: cartItems(cart),
    }),
  );
}

/** Commande créée — quel que soit le moyen de paiement. */
export function trackOrderCreated(
  cart: ApiCart,
  total: number,
  paymentMethod: string,
  orderUlid?: string,
): void {
  safely(() =>
    analytics.trackEvent("order_created_success", {
      currency: CURRENCY,
      value: total,
      store_ulid: cart.store_ulid,
      store_name: cart.store_name,
      payment_method: paymentMethod,
      ...(orderUlid ? { order_id: orderUlid } : {}),
    }),
  );
}

const TRACKED_KEY = "fast_tracked_purchases";

/** Une commande n'est comptée qu'une fois, même si la page de retour se recharge. */
function alreadyTracked(orderUlid: string): boolean {
  try {
    const seen: string[] = JSON.parse(localStorage.getItem(TRACKED_KEY) ?? "[]");
    if (seen.includes(orderUlid)) return true;
    localStorage.setItem(TRACKED_KEY, JSON.stringify([...seen.slice(-49), orderUlid]));
  } catch {
    // Stockage indisponible : on compte, au risque d'un doublon plutôt qu'un oubli.
  }
  return false;
}

/**
 * Achat réglé. Appelé à la création pour les paiements internes (espèces,
 * portefeuille), et au retour de la passerelle SATIM pour la carte — jamais
 * avant que l'argent soit réellement acquis.
 */
export function trackPurchase(params: {
  orderUlid?: string;
  total: number;
  paymentMethod: string;
  storeUlid?: string;
  storeName?: string;
  items?: GA4Item[];
  coupon?: string;
}): void {
  safely(() => {
    if (params.orderUlid && alreadyTracked(params.orderUlid)) return;
    analytics.trackPurchase(params.total, CURRENCY, {
      ...(params.orderUlid ? { transaction_id: params.orderUlid, order_id: params.orderUlid } : {}),
      payment_method: params.paymentMethod,
      ...(params.storeUlid ? { store_ulid: params.storeUlid } : {}),
      ...(params.storeName ? { store_name: params.storeName, affiliation: params.storeName } : {}),
      ...(params.coupon ? { coupon: params.coupon } : {}),
      items: params.items ?? [],
    });
  });
}

export { cartItems as ga4CartItems };
